import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { colors } from '../styles/colors';
import { MapPin, Wifi, ArrowLeft, Linkedin, Mail } from 'lucide-react';

const AccountManagerPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
    // Intentional: mount-only effect; deps intentionally omitted
  }, []);

  const linkedInUrl = 'https://www.linkedin.com/jobs/view/4396396466';
  const emailSubject = 'Head of Account Management Application - Arizore';
  const emailAddress = 'George@azreserves.com';

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="pt-24 sm:pt-28 pb-12 sm:pb-16 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.black} 0%, ${colors.graphite} 100%)`,
        }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Back link */}
          <Link 
            to="/careers"
            className={`inline-flex items-center gap-2 text-sm mb-8 transition-all duration-300 hover:opacity-100 ${
              isVisible ? 'opacity-60' : 'opacity-0'
            }`}
            style={{ color: colors.white }}
          >
            <ArrowLeft size={16} />
            Back to Careers
          </Link>

          <h1 
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-4 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ color: colors.white }}
          >
            Head of Account Management
          </h1>

          {/* Role meta */}
          <div 
            className={`flex flex-wrap items-center gap-4 sm:gap-6 mb-8 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '100ms' }}
          >
            <span 
              className="flex items-center gap-2 text-sm sm:text-base"
              style={{ color: colors.white, opacity: 0.7 }}
            >
              <MapPin size={18} />
              Australia
            </span>
            <span 
              className="flex items-center gap-2 text-sm sm:text-base"
              style={{ color: colors.white, opacity: 0.7 }}
            >
              <Wifi size={18} />
              Remote
            </span>
          </div>

          {/* Apply CTAs */}
          <div 
            className={`flex flex-col sm:flex-row gap-3 sm:gap-4 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <a
              href={linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-300 hover:opacity-90"
              style={{ 
                backgroundColor: '#0A66C2',
                color: colors.white
              }}
            >
              <Linkedin size={18} />
              Apply via LinkedIn
            </a>
            <a
              href={`mailto:${emailAddress}?subject=${encodeURIComponent(emailSubject)}`}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-sm border transition-all duration-300 hover:bg-white/10"
              style={{ 
                borderColor: `${colors.white}40`,
                color: colors.white
              }}
            >
              <Mail size={18} />
              Apply by Email
            </a>
          </div>
        </div>
      </section>

      {/* Job Description */}
      <section className="py-12 sm:py-16" style={{ backgroundColor: colors.white }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          
          {/* Email application note */}
          <div 
            className={`mb-10 p-5 rounded-xl border transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ 
              borderColor: `${colors.graphite}15`,
              backgroundColor: '#F8F8F8',
              transitionDelay: '300ms'
            }}
          >
            <p className="text-sm sm:text-base" style={{ color: colors.graphite }}>
              <span className="font-medium">To apply by email:</span>{' '}
              Please send your CV & Cover Letter to{' '}
              <a 
                href={`mailto:${emailAddress}?subject=${encodeURIComponent(emailSubject)}`}
                className="font-medium underline hover:no-underline"
                style={{ color: colors.graphite }}
              >
                {emailAddress}
              </a>
            </p>
          </div>

          {/* About the job */}
          <div 
            className={`mb-10 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '400ms' }}
          >
            <h2 
              className="text-xl sm:text-2xl font-bold mb-4"
              style={{ color: colors.graphite }}
            >
              Company Description
            </h2>
            <div className="space-y-4 text-base leading-relaxed" style={{ color: colors.graphite, opacity: 0.85 }}>
              <p>
                Arizore is building infrastructure that connects verified real-world assets to modern financial systems.
              </p>
              <p>
                We work directly with asset owners, partners, and capital providers to structure and deliver asset-backed opportunities through a transparent and verifiable framework.
              </p>
              <p>
                Our focus is on long-term trust, accurate asset representation, and enabling real-world value to operate within modern financial environments.
              </p>
            </div>
          </div>

          {/* Role Description */}
          <div 
            className={`mb-10 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '500ms' }}
          >
            <h2 
              className="text-xl sm:text-2xl font-bold mb-4"
              style={{ color: colors.graphite }}
            >
              Role Description
            </h2>
            <div className="space-y-4 text-base leading-relaxed" style={{ color: colors.graphite, opacity: 0.85 }}>
              <p>
                This is not a traditional account management role.
              </p>
              <p>
                As the Head of Account Management at Arizore, you will take ownership of opportunities from initial onboarding through to execution and ongoing relationship management.
              </p>
              <p>
                You will work directly with asset owners and partners, supporting the structuring, coordination, and delivery of complex, asset-backed transactions.
              </p>
              <p>
                The role sits at the intersection of commercial relationships, financial structuring, and operational delivery — requiring strong communication, attention to detail, and the ability to manage multiple stakeholders across the lifecycle of a deal.
              </p>
            </div>
          </div>

          {/* Key Responsibilities */}
          <div 
            className={`mb-10 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '600ms' }}
          >
            <h2 
              className="text-xl sm:text-2xl font-bold mb-4"
              style={{ color: colors.graphite }}
            >
              Key Responsibilities
            </h2>
            <ul className="space-y-3 text-base leading-relaxed" style={{ color: colors.graphite, opacity: 0.85 }}>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>Manage relationships with clients from onboarding through to delivery and ongoing support</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>Support the structuring of opportunities, including financial modelling inputs and scenario planning</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>Act as a central point of coordination between clients, technical teams, and compliance functions</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>Oversee onboarding and due diligence processes, ensuring all required documentation is collected and validated</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>Support execution processes including contract issuance, coordination with custody, and transaction delivery</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>Maintain ongoing client relationships post-onboarding, including managing future drawdowns and adjustments</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>Ensure all activity aligns with internal processes, compliance requirements, and operational standards</span>
              </li>
            </ul>
          </div>

          {/* Qualifications */}
          <div 
            className={`mb-10 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '700ms' }}
          >
            <h2 
              className="text-xl sm:text-2xl font-bold mb-4"
              style={{ color: colors.graphite }}
            >
              Qualifications
            </h2>
            <ul className="space-y-3 text-base leading-relaxed" style={{ color: colors.graphite, opacity: 0.85 }}>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>Experience in account management, client relationship management, or similar roles</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>Background in financial services, commodities, mining, or structured environments is highly desirable</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>Comfortable working with complex processes involving multiple stakeholders</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>Strong organisational and communication skills</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>Ability to translate technical or financial concepts into clear client-facing discussions</span>
              </li>
              <li className="flex gap-3">
                <span className="text-gray-400 mt-1.5">•</span>
                <span>High attention to detail, particularly in documentation and process management</span>
              </li>
            </ul>
          </div>

          {/* Bottom CTA */}
          <div 
            className={`pt-8 border-t transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ borderColor: `${colors.graphite}15`, transitionDelay: '800ms' }}
          >
            <h3 
              className="text-lg font-semibold mb-4"
              style={{ color: colors.graphite }}
            >
              Ready to apply?
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href={linkedInUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-300 hover:opacity-90"
                style={{ 
                  backgroundColor: '#0A66C2',
                  color: colors.white
                }}
              >
                <Linkedin size={18} />
                Apply via LinkedIn
              </a>
              <a
                href={`mailto:${emailAddress}?subject=${encodeURIComponent(emailSubject)}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium text-sm border transition-all duration-300"
                style={{ 
                  borderColor: `${colors.graphite}30`,
                  color: colors.graphite
                }}
              >
                <Mail size={18} />
                Apply by Email
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AccountManagerPage;
